// Phase 40 Plan 03 Task 2 — Multi-device conflict modal (D-15).
// UI-SPEC §5. 40-PATTERNS lines 224-237.
//
// Blocking modal — cannot be dismissed by scrim or X. User MUST choose
// either "Use cloud data" (silent download) or "Replace cloud with this
// device's data" (destructive, gated by inline second-step confirm).
//
// Props:
//   local:  { plants: number, areas?: number }
//   cloud:  { plants: number, areas?: number, last_modified?: string }
//   onChoose: (choice: 'cloud' | 'local') => void
//
// No merge UI — Tend deliberately avoids garden merge ambiguity (D-15).

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Cloud, Smartphone } from 'lucide-react';

function formatDate(iso) {
  if (!iso) return 'recently';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return 'recently'; }
}

export default function MultiDeviceConflictModal({ local, cloud, onChoose }) {
  const [step, setStep] = useState('select'); // 'select' | 'confirm-replace'

  const cloudPlants = cloud?.plants ?? 0;
  const localPlants = local?.plants ?? 0;
  const cloudWhen = formatDate(cloud?.last_modified);
  const localWhen = formatDate(local?.last_modified);

  // 2026-05-11 — portal to document.body so the modal escapes any parent
  // stacking context (Phase 48 BrandHero, YearView z-index wrapper, App
  // transforms) that was making the modal render BELOW the Year view
  // calendar. zIndex bumped to 2200 (above FirstSignInUploadModal's 2100)
  // as a belt-and-braces guard against any future high-z-index banner.
  // See Phase 40.2.09 — surfaced post-fix during the 2026-05-11 UAT.
  const modal = (
    <div
      // NOTE: no onClick={onCancel} — non-dismissible per UI-SPEC #5.
      style={{
        position: 'fixed', inset: 0, background: 'var(--c-overlay)', zIndex: 2200,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="conflict-modal-title"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--c-bg)', borderRadius: 18, padding: 28,
          maxWidth: 360, width: '100%',
          boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
        }}
      >
        <h2
          id="conflict-modal-title"
          style={{
            fontFamily: "'Playfair Display', serif", fontSize: 'var(--font-h3)',
            color: 'var(--c-heading)', margin: '0 0 12px', fontWeight: 700,
          }}
        >
          Two gardens found
        </h2>

        {step === 'select' && (
          <>
            <p style={{ fontSize: 'var(--font-body)', color: 'var(--c-text)', margin: '0 0 20px', lineHeight: 1.5 }}>
              We found a garden saved on this device <em>and</em> one already in the cloud.
              Which one would you like to keep? The other will be replaced.
            </p>

            {/* Comparison rail — two stacked cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              <div style={{
                background: 'var(--c-surface)', borderRadius: 12, padding: 16,
                display: 'flex', alignItems: 'flex-start', gap: 12,
              }}>
                <Cloud size={20} style={{ color: 'var(--c-brand-deep)', flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ margin: 0, fontSize: 'var(--font-body)', fontWeight: 600, color: 'var(--c-heading)' }}>
                    In the cloud
                  </h3>
                  <small style={{ color: 'var(--c-text-2)', fontSize: 'var(--font-small)' }}>
                    <em>{cloudPlants}</em> plants · last updated <em>{cloudWhen}</em>
                  </small>
                </div>
              </div>
              <div style={{
                background: 'var(--c-surface)', borderRadius: 12, padding: 16,
                display: 'flex', alignItems: 'flex-start', gap: 12,
              }}>
                <Smartphone size={20} style={{ color: 'var(--c-brand-deep)', flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ margin: 0, fontSize: 'var(--font-body)', fontWeight: 600, color: 'var(--c-heading)' }}>
                    On this device
                  </h3>
                  <small style={{ color: 'var(--c-text-2)', fontSize: 'var(--font-small)' }}>
                    <em>{localPlants}</em> plants · last edited <em>{localWhen}</em>
                  </small>
                </div>
              </div>
            </div>

            {/* Primary — silent cloud download */}
            <button
              type="button"
              onClick={() => onChoose('cloud')}
              style={{
                background: 'var(--c-brand-deep)', color: 'var(--c-accent-text)',
                border: 'none', borderRadius: 22, padding: '12px 20px',
                fontSize: 'var(--font-body)', fontWeight: 600, cursor: 'pointer',
                width: '100%', minHeight: 44, marginBottom: 10,
                fontFamily: 'inherit',
              }}
            >
              Use cloud data
            </button>

            {/* Destructive — replace cloud with device data (two-step) */}
            <button
              type="button"
              onClick={() => setStep('confirm-replace')}
              style={{
                background: 'var(--c-bg)', color: 'var(--c-danger)',
                border: '1px solid var(--c-danger)', borderRadius: 22,
                padding: '12px 20px', fontSize: 'var(--font-body)', fontWeight: 600,
                cursor: 'pointer', width: '100%', minHeight: 44,
                fontFamily: 'inherit',
              }}
            >
              Replace cloud with this device's data
            </button>

            <small className="t-tiny" style={{
              display: 'block', marginTop: 16, textAlign: 'center',
              color: 'var(--c-text-3)', fontSize: 'var(--font-tiny)', lineHeight: 1.5,
            }}>
              Tend doesn't merge gardens. Pick one — the other will be replaced.
            </small>
          </>
        )}

        {step === 'confirm-replace' && (
          <>
            <p style={{ fontSize: 'var(--font-body)', color: 'var(--c-text)', margin: '0 0 20px', lineHeight: 1.5 }}>
              This permanently replaces your cloud garden. Continue?
            </p>
            <button
              type="button"
              onClick={() => onChoose('local')}
              style={{
                background: 'var(--c-danger)', color: 'var(--c-danger-text)',
                border: 'none', borderRadius: 22, padding: '12px 20px',
                fontSize: 'var(--font-body)', fontWeight: 600, cursor: 'pointer',
                width: '100%', minHeight: 44, marginBottom: 10,
                fontFamily: 'inherit',
              }}
            >
              Replace
            </button>
            <button
              type="button"
              onClick={() => setStep('select')}
              style={{
                background: 'var(--c-btn-bg)', color: 'var(--c-btn-text)',
                border: 'none', borderRadius: 22, padding: '12px 20px',
                fontSize: 'var(--font-body)', fontWeight: 600, cursor: 'pointer',
                width: '100%', minHeight: 44, fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );

  // SSR / test guard — if document isn't available, return inline.
  return typeof document !== 'undefined' ? createPortal(modal, document.body) : modal;
}
